import { useTranslation } from 'react-i18next';

import { AxiosError } from 'axios';

import {
  DeliveryAddressRequest,
  DeliveryAddressResponse,
  GenerateAwbRequest,
  GenerateAwbResponse,
  PickupOrderResponse,
  PlacePickupOrderRequest,
  ShipmentResponse,
  TariffPreviewRequest,
  TariffQuote,
} from '@/api/data-contracts';
import { queryKeys } from '@/react-query/constants';
import { getBackendErrorMessage } from '@/shared/lib/errors';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

import { courierRejectionOf, translateCourierRejection } from '../model/courierRejection';
import { useShippingService } from './shipping.service';
import { shippingApi } from './shippingApi';

export interface GenerateAwbVariables {
  taskId: string;
  request: GenerateAwbRequest;
}

export const useGenerateAwbMutation = () => {
  const { t } = useTranslation();
  const { generateAwb } = useShippingService();
  const { enqueueSnackbar } = useSnackbar();

  return shippingApi.useFeatureMutation<GenerateAwbResponse, GenerateAwbVariables>({
    mutationFn: ({ taskId, request }) => generateAwb(taskId, request),
    invalidate: [shippingApi.keys.all, [queryKeys.tasks], [queryKeys.serviceOrders]],
    onError: (error) => {
      const rejection = courierRejectionOf(error);
      enqueueSnackbar(
        rejection
          ? translateCourierRejection(rejection, t)
          : getBackendErrorMessage(error as AxiosError),
        { variant: 'error' },
      );
    },
  });
};

export interface VoidShipmentVariables {
  id: string;
  reason?: string;
}

export const useVoidShipmentMutation = () => {
  const { voidShipment } = useShippingService();
  const { enqueueSnackbar } = useSnackbar();

  return shippingApi.useFeatureMutation<ShipmentResponse, VoidShipmentVariables>({
    mutationFn: ({ id, reason }) => voidShipment(id, reason),
    invalidate: [shippingApi.keys.all, [queryKeys.tasks], [queryKeys.serviceOrders]],
    successMessage: 'The AWB has been cancelled',
    onError: (error) =>
      enqueueSnackbar(getBackendErrorMessage(error as AxiosError), { variant: 'error' }),
  });
};

export const usePreviewTariffMutation = () => {
  const { previewShipmentTariff } = useShippingService();
  const { enqueueSnackbar } = useSnackbar();

  return shippingApi.useFeatureMutation<TariffQuote, TariffPreviewRequest>({
    mutationFn: (request) => previewShipmentTariff(request),
    invalidate: false,
    onError: (error) =>
      enqueueSnackbar(getBackendErrorMessage(error as AxiosError), { variant: 'error' }),
  });
};

export const usePlacePickupOrderMutation = () => {
  const { placePickupOrder } = useShippingService();
  const { enqueueSnackbar } = useSnackbar();

  return shippingApi.useFeatureMutation<PickupOrderResponse, PlacePickupOrderRequest>({
    mutationFn: (request) => placePickupOrder(request),
    invalidate: [shippingApi.keys.all],
    successMessage: 'The courier has been ordered',
    onError: (error) =>
      enqueueSnackbar(getBackendErrorMessage(error as AxiosError), { variant: 'error' }),
  });
};

export interface UpdateDeliveryAddressVariables {
  id: string;
  request: DeliveryAddressRequest;
}

export const useUpdateDeliveryAddressMutation = () => {
  const { updateDeliveryAddress } = useShippingService();
  const { enqueueSnackbar } = useSnackbar();

  return shippingApi.useFeatureMutation<DeliveryAddressResponse, UpdateDeliveryAddressVariables>({
    mutationFn: ({ id, request }) => updateDeliveryAddress(id, request),
    invalidate: [shippingApi.keys.all],
    successMessage: 'The delivery address has been saved',
    onError: (error) =>
      enqueueSnackbar(getBackendErrorMessage(error as AxiosError), { variant: 'error' }),
  });
};

export const useCreateDeliveryAddressMutation = () => {
  const { createDeliveryAddress } = useShippingService();
  const { enqueueSnackbar } = useSnackbar();

  return shippingApi.useFeatureMutation<DeliveryAddressResponse, DeliveryAddressRequest>({
    mutationFn: (request) => createDeliveryAddress(request),
    invalidate: [shippingApi.keys.all],
    successMessage: 'The delivery address has been saved',
    onError: (error) =>
      enqueueSnackbar(getBackendErrorMessage(error as AxiosError), { variant: 'error' }),
  });
};
